
import { Component, Emit, Prop, Vue } from 'nuxt-property-decorator';
import type { PropType } from 'vue';
import InnerImageZoom from 'vue-inner-image-zoom';
import { ColPosAppearance, Responsive } from '@/types/pageContract';
import { File, ImageMetaData } from '@/types/contentContract';
import TooltipButton from '@/components/ui/TooltipButton.vue';
import MetaDataForImageFirst from '~/components/typo3-library-overrides/media/MetaDataForImageFirst.vue';
import MetaDataForImageSecond from '~/components/typo3-library-overrides/media/MetaDataForImageSecond.vue';
import KeyHandleEventBus from '@/store/keyHandle.eventbus';

interface MetaDataFields {
  description: string;
  caption: string;
  copyright: string;
  credits: string;
}

@Component({
  components: {
    MetaDataForImageSecond,
    MetaDataForImageFirst,
    InnerImageZoom,
    TooltipButton
  }
})
export default class ImageOverlayCarousel extends Vue {
  @Prop({ type: Object as PropType<ColPosAppearance>, default: () => {} })
  appearance!: ColPosAppearance;

  @Prop({ type: Array as PropType<File[]> })
  images!: File[];

  @Prop({ type: Object as PropType<Responsive>, required: true, default: {} })
  responsive!: Responsive;

  @Prop({ type: Object as PropType<ImageMetaData>, default: () => {} })
  imageMetaData!: ImageMetaData;

  @Prop({ type: Number, default: 1 })
  indexFromCarousel!: number;

  preventZoom: boolean = false;
  swiperOverlayIndex: number = 0;
  isHoveringImage: boolean = false;
  activeSlide: number = 0;

  get currentImageSlide(): File | null {
    return this.images[this.activeSlide];
  }

  get currentImageFileHasLabel(): boolean {
    return this.mediaLabels[this.swiperOverlayIndex - 1] !== undefined;
  }

  get metaDataFields(): MetaDataFields | null {
    const imageToUse: File = this.currentImageSlide;

    if (!imageToUse) {
      return null;
    }

    return {
      description: imageToUse.properties.description,
      caption: imageToUse.properties.caption,
      copyright: imageToUse.properties.copyright
        ? imageToUse.properties.copyright
        : '',
      credits: imageToUse.properties.credits
    };
  }

  get currentImageHasMetaData(): boolean {
    if (this.metaDataFields) {
      return Object.keys(this.metaDataFields).some((key) => {
        return this.metaDataFields[key] !== '';
      });
    }

    return false;
  }

  /*
  The MediaLabels in the backend allows you to enter one label per image, separated by newlines
  */
  get mediaLabels(): Array<string> {
    return this.imageMetaData.medialabels
      ? this.imageMetaData.medialabels.split('\r\n')
      : [''];
  }

  @Emit('closeOverlay')
  closeOverlay(): boolean {
    return true;
  }

  mounted() {
    this.$nextTick(() => {
      this.activeSlide = this.indexFromCarousel;
      this.swiperOverlayIndex = this.indexFromCarousel;
      KeyHandleEventBus.$on('arrowleft', this.slidePrevOverlay);
      KeyHandleEventBus.$on('arrowright', this.slideNextOverlay);
      this.determineIfZoomIsAllowed();
    });
  }

  determineIfZoomIsAllowed(): void {
    const wideImage =
      this.responsive.wide[
        this.currentImageSlide.properties.fileReferenceUid as string
      ];

    this.preventZoom = this.isEqual(
      this.currentImageSlide.properties.dimensions,
      wideImage.properties.dimensions
    );
  }

  changeCarouselIndex(): void {
    this.determineIfZoomIsAllowed();
  }

  overlayImage(image: File): File {
    return this.responsive.wide[image.properties.fileReferenceUid as string];
  }

  isPngImage(url): string {
    if (url.endsWith('.png')) {
      return 'tw-bg-white';
    }

    return '';
  }

  slideNextOverlay(): void {
    this.$nextTick(() => {
      this.activeSlide++;
    });
  }

  slidePrevOverlay(): void {
    this.$nextTick(() => {
      this.activeSlide--;
    });
  }

  private isEqual(...objects: object[]): boolean {
    return objects.every(
      (obj) => JSON.stringify(obj) === JSON.stringify(objects[0])
    );
  }
}
